import { safeParseNullDefault, safeParse } from 'shouldve-been-js';
import { datetimeToApiString } from '../Utils/DateTimeParser';
import type ObservationModel from './ObservationModelModel';

class FilledObservationLine {

    private _observationLineId: string;
    private _subChapterId: string;
    private _priority: number;
    private _inspectorComment: string;
    private _generalComment: string;
    private _rootCause: string;
    private _correctiveAction: string;
    private _preventiveAction: string;
    private _additionalComment: string;
    private _createdDate: Date;
    private _custom1: string;
    private _custom2: string;
    private _custom3: string;
    private _custom4: string;

    public constructor(observationLineId: string, subChapterId: string, priority: number, inspectorComment: string, generalComment: string, rootCause: string, correctiveAction: string, preventiveAction: string, additionalComment: string, createdDate: Date, custom1: string, custom2: string, custom3: string, custom4: string) {

        this._observationLineId = observationLineId;
        this._subChapterId = subChapterId;
        this._priority = priority;
        this._inspectorComment = inspectorComment;
        this._generalComment = generalComment;
        this._rootCause = rootCause;
        this._correctiveAction = correctiveAction;
        this._preventiveAction = preventiveAction;
        this._additionalComment = additionalComment;
        this._createdDate = createdDate;
        this._custom1 = custom1;
        this._custom2 = custom2;
        this._custom3 = custom3;
        this._custom4 = custom4;
    }

    public get observationLineId(): string {
        return this._observationLineId;
    }

    public set observationLineId(value: string) {
        this._observationLineId = value;
    }

    public get subChapterId(): string {
        return this._subChapterId;
    }

    public set subChapterId(value: string) {
        this._subChapterId = value;
    }

    public get priority(): number {
        return this._priority;
    }

    public set priority(value: number) {
        this._priority = value;
    }

    public get inspectorComment(): string {
        return this._inspectorComment;
    }

    public set inspectorComment(value: string) {
        this._inspectorComment = value;
    }

    public get generalComment(): string {
        return this._generalComment;
    }

    public set generalComment(value: string) {
        this._generalComment = value;
    }

    public get rootCause(): string {
        return this._rootCause;
    }

    public set rootCause(value: string) {
        this._rootCause = value;
    }

    public get correctiveAction(): string {
        return this._correctiveAction;
    }

    public set correctiveAction(value: string) {
        this._correctiveAction = value;
    }

    public get preventiveAction(): string {
        return this._preventiveAction;
    }

    public set preventiveAction(value: string) {
        this._preventiveAction = value;
    }

    public get additionalComment(): string {
        return this._additionalComment;
    }

    public set additionalComment(value: string) {
        this._additionalComment = value;
    }

    public get createdDate(): Date {
        return this._createdDate;
    }

    public set createdDate(value: Date) {
        this._createdDate = value;
    }

    public get custom1(): string {
        return this._custom1;
    }

    public set custom1(value: string) {
        this._custom1 = value;
    }

    public get custom2(): string {
        return this._custom2;
    }

    public set custom2(value: string) {
        this._custom2 = value;
    }

    public get custom3(): string {
        return this._custom3;
    }

    public set custom3(value: string) {
        this._custom3 = value;
    }

    public get custom4(): string {
        return this._custom4;
    }

    public set custom4(value: string) {
        this._custom4 = value;
    }

    public getFirstFieldSummary(observationModel: ObservationModel | null, maxCharacters: number): string { //used to give a quick text/summary to identify the observation line

        if (observationModel === null) {
            return '';
        }

        let lowestSortNumber: number = 10000000;
        let associatedValue: string = '';
        
        if (observationModel.inspectorCommentActive && this.inspectorComment !== '' && observationModel.inspectorCommentSortNumber < lowestSortNumber) {
            lowestSortNumber = observationModel.inspectorCommentSortNumber;
            associatedValue = this.inspectorComment;
        }

        if (observationModel.generalCommentActive && this.generalComment !== '' && observationModel.generalCommentSortNumber < lowestSortNumber) {
            lowestSortNumber = observationModel.generalCommentSortNumber;
            associatedValue = this.generalComment;
        }

        if (observationModel.rootCauseActive && this.rootCause !== '' && observationModel.rootCauseSortNumber < lowestSortNumber) {
            lowestSortNumber = observationModel.rootCauseSortNumber;
            associatedValue = this.rootCause;
        }

        if (observationModel.correctiveActionActive && this.correctiveAction !== '' && observationModel.correctiveActionSortNumber < lowestSortNumber) {
            lowestSortNumber = observationModel.correctiveActionSortNumber;
            associatedValue = this.correctiveAction;
        }

        if (observationModel.preventiveActionActive && this.preventiveAction !== '' && observationModel.preventiveActionSortNumber < lowestSortNumber) {
            lowestSortNumber = observationModel.preventiveActionSortNumber;
            associatedValue = this.preventiveAction;
        }

        if (observationModel.additionalCommentActive && this.additionalComment !== '' && observationModel.additionalCommentSortNumber < lowestSortNumber) {
            lowestSortNumber = observationModel.additionalCommentSortNumber;
            associatedValue = this.additionalComment;
        }

        if (associatedValue === '') {
            return '<empty>';
        }

        return associatedValue.substring(0, maxCharacters);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    public static fromIndexedDB(object: any): FilledObservationLine | null {

        const observationLineId: string | null = safeParseNullDefault<string>(object?._observationLineId, '');
        const subChapterId: string = safeParse<string>(object?._subChapterId, '');
        const priority: number = safeParse<number>(object?._priority, 0);
        const inspectorComment: string = safeParse<string>(object?._inspectorComment, '');
        const generalComment: string = safeParse<string>(object?._generalComment, '');
        const rootCause: string = safeParse<string>(object?._rootCause, '');
        const correctiveAction: string = safeParse<string>(object?._correctiveAction, '');
        const preventiveAction: string = safeParse<string>(object?._preventiveAction, '');
        const additionalComment: string = safeParse<string>(object?._additionalComment, '');
        const createdDate: Date | null = safeParseNullDefault<Date>(object?._createdDate, new Date());
        const custom1: string = safeParse<string>(object?._custom1, '');
        const custom2: string = safeParse<string>(object?._custom2, '');
        const custom3: string = safeParse<string>(object?._custom3, '');
        const custom4: string = safeParse<string>(object?._custom4, '');

        if (observationLineId === null || createdDate === null || observationLineId === '') {
            return null;
        }

        return new FilledObservationLine(observationLineId, subChapterId, priority, inspectorComment, generalComment, rootCause, correctiveAction, preventiveAction, additionalComment, createdDate, custom1, custom2, custom3, custom4);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
    public toJson(serverTimezoneOffsetMinutes: number): any {

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const jsonObject: any = {
            subChapterId: this.subChapterId,
            priority: this.priority,
            inspectorComment: this.inspectorComment,
            generalComment: this.generalComment,
            rootCause: this.rootCause,
            correctiveAction: this.correctiveAction,
            preventiveAction: this.preventiveAction,
            additionalComment: this.additionalComment,
            createdDate: datetimeToApiString(this.createdDate, serverTimezoneOffsetMinutes),
            custom1: this.custom1,
            custom2: this.custom2,
            custom3: this.custom3,
            custom4: this.custom4,
        };

        return jsonObject;
    }
}

export default FilledObservationLine;